import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { DataModules } from "@/store/modules/data";
import eventsMap from "@/components/app/global/timelineComponent/events";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { mapState } from "vuex";
import {
  ITimelineProvider,
  ITimelineProviderMethods
} from "@/models/providers/timeline";
import { IClient } from "@/models/clients";
import { IAccount } from "@/models/accounts";
import { IBrand } from "@/models/brands";
import { UUID } from "@/data/enums";
import { IUser } from "@/models/users";
import { IHookLog } from "@/models/hooks";

export const enum TimelineType {
  TABLE = "table",
  STREAM = "stream"
}

@Component({
  computed: {
    ...mapState({
      events(state, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.HOOKS_LOGS,
          scope: this.dataScope
        });
      }
    })
  }
})
export default class TimelineProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props
  @Prop({ default: UUID.ORG })
  public brandId!: IBrand["id"];

  @Prop({ default: "" })
  public accountId!: IAccount["id"];

  @Prop({ default: "" })
  public clientId!: IClient["id"];

  @Prop({ default: "" })
  public userId!: IUser["id"];

  @Prop({ default: 25 })
  public declare limit: number;

  @Prop({ default: null })
  public type!: string;

  @Prop({ default: null })
  public typeLike!: string;

  @Prop({ default: null })
  public objectId!: string;

  @Prop({ default: null })
  public hookCode!: string;

  @Prop({ default: TimelineType.STREAM })
  public timelineType!: string;

  // Data
  public events!: IHookLog[];
  public dataModule: DataModules = DataModules.HOOKS_LOGS;
  public hasMoreEvents = false;
  public initialLoad = true;
  public isLoading = true;
  public isLoadingToMatchLimit = false;
  public localBrandId = this.brandId;
  public hiddenEvents = {};
  public pastHiddenEvents = {};

  public streamConfig = {
    limit: this.limit,
    offset: 0,
    page: 1
  };

  @Provide()
  public timelineProvider(): ITimelineProvider {
    return {
      events: this.events,
      mappedEvents: this.mappedEvents,
      isStreamView: this.isStreamView,
      isTableView: this.isTableView,
      type: this.type,
      objectId: this.objectId,
      userId: this.userId
    };
  }

  @Provide()
  public get timelineProviderMethods(): ITimelineProviderMethods {
    return {
      markHidden: this.onMarkHidden,
      isSameEvent: this.isSameEvent,
      openLogModal: this.openLogModal,
      getDateFormat: this.getDateFormat
    };
  }

  // Getters
  public get isStreamView() {
    return this.timelineType === TimelineType.STREAM;
  }

  public get isTableView() {
    return this.timelineType === TimelineType.TABLE;
  }

  public get orderedEvents() {
    if (this.isTableView) return this.events;
    return this.$_.orderBy(this.events, ["created_at"], ["desc"]);
  }

  public get visibleEvents() {
    return this.mappedEvents.filter(e => !e.isHidden);
  }

  public get mToday() {
    return this.$moment();
  }

  public get mYesterday() {
    return this.$moment().add(-1, "day");
  }

  public get mappedEvents() {
    return this.orderedEvents.map((event, index) => {
      const code = this.$_.get(event, "hook.code", "");
      const eventMap = this.$_.get(eventsMap, code, {
        event: () => ({
          timestamp: event.created_at,
          message: this.$_.get(
            event,
            "hook.name",
            this.$_.get(event, "object_type")
          )
        })
      });
      return {
        ...eventMap.event(event),
        id: event.id,
        actor: event.actor,
        object: event.object,
        object_type: event.object_type,
        object_brand_id: event.object_brand_id,
        object_id: event.object_id,
        hook: event.hook,
        sourceEvent: event,
        isHidden: this.hiddenEvents[event.id] || false,
        wasHidden: this.pastHiddenEvents[event.id] || false,
        index
      };
    });
  }

  // Methods
  public async initTimelineProvider() {
    if (this.$userCan("list_brand_hook_logs")) {
      try {
        await this.getEvents();
      } finally {
        this.initialLoad = false;
        this.loadMoreUntilVisibleMatchLimit();
      }
    }
  }

  public onMarkHidden({ event, isHidden }) {
    if (!this.isStreamView) return;
    if (event) {
      if (isHidden) {
        this.$set(this.hiddenEvents, event.id, true);
        this.$set(this.pastHiddenEvents, event.id, true);
      } else {
        this.$delete(this.hiddenEvents, event.id);
      }
    }
  }

  public loadMoreUntilVisibleMatchLimit() {
    this.$nextTick(async () => {
      if (this.hasMoreEvents && this.visibleEvents.length < this.limit) {
        this.isLoadingToMatchLimit = true;
        try {
          await this.loadMore();
        } finally {
          this.loadMoreUntilVisibleMatchLimit();
          this.isLoadingToMatchLimit = false;
        }
      }
    });
  }

  public getFilterForHookCode() {
    return this.hookCode ? { "filter[hook.code|like]": this.hookCode } : {};
  }

  public getFilterForType() {
    return !this.$_.isEmpty(this.type)
      ? { "filter[object_type]": this.type }
      : {};
  }

  public getFilterForTypeLike() {
    return !this.$_.isEmpty(this.typeLike)
      ? { "filter[object_type|like]": this.typeLike }
      : {};
  }

  public getFilterForObject() {
    return this.objectId ? { "filter[object_id]": this.objectId } : {};
  }

  public getEventsPaginated(config) {
    return this.getData(config);
  }

  public async getEventsAsStream(config) {
    try {
      this.isLoading = true;
      this.apiProps = {
        offset: this.events.length,
        order: "-created_at"
      };
      this.dataPayloadSettings = {
        merge: true,
        ignoreStored: true
      };
      await this.getData(config);
      this.hasMoreEvents = this.events.length < this.table.total;
    } finally {
      this.isLoading = false;
    }
  }

  public getEvents(config = {}) {
    if (this.isTableView) return this.getEventsPaginated(config);
    return this.getEventsAsStream(config);
  }

  // overrides mixin method
  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(params, {
      scope: this.dataScope,
      userId: this.userId,
      brandId: this.localBrandId,
      accountId: this.accountId,
      clientId: this.clientId,
      returnData: false,
      params: {
        ...this.getFilterForType(),
        ...this.getFilterForTypeLike(),
        ...this.getFilterForObject(),
        ...this.getFilterForHookCode(),
        "filter[hook.id|gt]": 0, // filter out ones with hook = null
        brandId: this.localBrandId,
        with: "hook,actor,object"
      }
    });
  }

  public loadMore() {
    if (this.hasMoreEvents && !this.isLoading) {
      return this.getEvents();
    }
    return Promise.resolve();
  }

  public isSameEvent(event, other) {
    if (!other) return false;
    return (
      event.object_brand_id === other.object_brand_id &&
      event.object_type === other.object_type &&
      event.object_id === other.object_id &&
      this.withinSameTimeframe(event, other, 5)
    );
  }

  public getDateFormat(event) {
    if (this.$moment(event.timestamp).isSame(this.mToday, "day"))
      return this.$t("_datetime.time.short") as string;
    else if (this.$moment(event.timestamp).isSame(this.mYesterday, "day"))
      return this.$t("_datetime.calendar.lastDay") as string;
    return this.$t("_datetime.month.short") as string;
  }

  /** Checks if both objects timestamps overlap within defined range in minutes*/
  public withinSameTimeframe(event, other, minutes) {
    if (!other) return false;
    const current = this.$moment(event.timestamp);
    return (
      current.isSameOrBefore(other.timestamp) &&
      current
        .clone()
        .add(minutes, "minutes")
        .isSameOrAfter(other.timestamp)
    );
  }

  public openLogModal(data) {
    this.$store.dispatch("ui/open/slideModal", {
      config: {
        component: () =>
          import(
            "@/components/app/global/timelineComponent/timelineLogModal.vue"
          ),
        props: {
          title: this.$t("_.details"),
          data,
          deep: 1
        }
      }
    });
  }
}
